import { useState } from 'react';
import { P } from '@stockpilot/shared';
import { useFormat } from '@/hooks/useAccess';
import {
  useAccountsQuery, useCreateGoodsReceiptMutation, useGoodsReceiptActionMutation,
  usePurchaseOrderActionMutation, usePurchaseOrderQuery,
} from '@/api/endpoints';
import { apiErrorMessage } from '@/api/baseApi';
import { formatDate, formatMoney, formatQuantity } from '@/lib/format';
import {
  Alert, Badge, Button, ErrorState, Input, LoadingState, Modal, Select, Table, Td, Th,
} from '@/components/ui';
import { Can } from '@/components/Can';
import { PO_TONE } from './PurchaseOrdersPage';

/**
 * One order, with the actions its status allows and a receiving panel.
 *
 * Receiving is done from here rather than a separate screen because the
 * outstanding quantities live on the order — asking "what still hasn't arrived"
 * anywhere else means re-deriving them.
 */
export function PurchaseOrderDetailModal({
  orderId,
  onClose,
}: {
  orderId: string;
  onClose: () => void;
}) {
  const format = useFormat();
  const { data: order, isLoading, isError, error, refetch } = usePurchaseOrderQuery(orderId);

  const { data: accounts } = useAccountsQuery({ limit: 50 });
  const [runAction, actionState] = usePurchaseOrderActionMutation();
  const [createReceipt, receiptState] = useCreateGoodsReceiptMutation();
  const [receiptAction] = useGoodsReceiptActionMutation();

  const [failure, setFailure] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [receiving, setReceiving] = useState(false);
  // Approving commits the money, so the account paying for it is chosen here
  // rather than left until someone tries to settle the invoice.
  const [approving, setApproving] = useState(false);
  const [payingAccountId, setPayingAccountId] = useState('');
  const [quantities, setQuantities] = useState<Record<string, string>>({});
  const [deliveryNote, setDeliveryNote] = useState('');

  const act = async (action: string, body?: Record<string, unknown>): Promise<void> => {
    setFailure(null);
    setNotice(null);
    try {
      await runAction({ id: orderId, action, body }).unwrap();
    } catch (actionError) {
      setFailure(apiErrorMessage(actionError, `The order could not be ${action}ed`));
    }
  };

  /** Creates the receipt and posts it in one go — the counter case is one step. */
  const receive = async (): Promise<void> => {
    setFailure(null);
    setNotice(null);

    const lines = Object.entries(quantities)
      .filter(([, value]) => value !== '' && Number(value) > 0)
      .map(([orderItemId, value]) => ({ orderItemId, quantity: Number(value) }));

    if (lines.length === 0) {
      setFailure('Enter what actually arrived on at least one line');
      return;
    }

    try {
      const receipt = await createReceipt({
        orderId,
        supplierDeliveryNote: deliveryNote || undefined,
        lines,
      }).unwrap();

      await receiptAction({ id: receipt.id, action: 'post' }).unwrap();

      setNotice(`Received on ${receipt.number}. Stock is in at the cost paid.`);
      setReceiving(false);
      setQuantities({});
      setDeliveryNote('');
    } catch (receiveError) {
      setFailure(apiErrorMessage(receiveError, 'That delivery could not be received'));
    }
  };

  const status = order?.status;
  const receivable =
    status === 'APPROVED' || status === 'ORDERED' || status === 'PARTIALLY_RECEIVED';

  return (
    <Modal
      open
      onClose={onClose}
      title={order ? `Order ${order.number}` : 'Purchase order'}
      description={order ? order.supplier.name : undefined}
      size="lg"
      footer={
        <>
          <Button variant="outline" onClick={onClose}>Close</Button>

          {status === 'DRAFT' && (
            <Can permission={P.PURCHASE_CREATE}>
              <Button loading={actionState.isLoading} onClick={() => void act('submit')}>
                Submit for approval
              </Button>
            </Can>
          )}
          {status === 'SUBMITTED' && (
            <Can permission={P.PURCHASE_APPROVE}>
              <Button onClick={() => setApproving(true)}>Approve</Button>
            </Can>
          )}
          {status === 'APPROVED' && (
            <Can permission={P.PURCHASE_APPROVE}>
              <Button loading={actionState.isLoading} onClick={() => void act('order')}>
                Mark as placed
              </Button>
            </Can>
          )}
          {receivable && !receiving && (
            <Can permission={P.PURCHASE_RECEIVE}>
              <Button onClick={() => setReceiving(true)}>Receive goods</Button>
            </Can>
          )}
        </>
      }
    >
      {isLoading ? (
        <LoadingState />
      ) : isError ? (
        <ErrorState message={apiErrorMessage(error)} onRetry={() => void refetch()} />
      ) : order ? (
        <div className="space-y-4">
          {failure && <Alert tone="danger">{failure}</Alert>}
          {notice && <Alert tone="success">{notice}</Alert>}

          <div className="flex flex-wrap items-center gap-3">
            <Badge tone={PO_TONE[order.status]}>
              {order.status.toLowerCase().replace(/_/g, ' ')}
            </Badge>
            <span className="text-xs text-muted">Raised {formatDate(order.createdAt, format)}</span>
            {order.expectedDate && (
              <span className="text-xs text-muted">
                Expected {formatDate(order.expectedDate, format)}
              </span>
            )}
            {order.payingAccount && (
              <span className="text-xs text-muted">
                Paid from <strong className="font-medium">{order.payingAccount.name}</strong>
              </span>
            )}
          </div>

          {receiving && (
            <div className="space-y-3 rounded-lg border border-subtle bg-sunken p-3">
              <p className="text-sm font-medium">What arrived</p>
              <p className="text-xs text-muted">
                Posting puts the goods into stock at the cost on the order, which is what moves the
                product&apos;s average cost.
              </p>

              {order.items?.map((item) => (
                <div key={item.id} className="flex flex-wrap items-end gap-3">
                  <div className="min-w-44 flex-1">
                    <p className="text-sm">{item.description}</p>
                    <p className="text-xs text-muted">
                      {formatQuantity(item.outstandingQty, format)} of{' '}
                      {formatQuantity(item.quantity, format)} still outstanding
                    </p>
                  </div>
                  <div className="w-28">
                    <Input
                      label="Received"
                      type="number"
                      min="0"
                      max={item.outstandingQty}
                      step="0.0001"
                      value={quantities[item.id] ?? ''}
                      onChange={(event) =>
                        setQuantities({ ...quantities, [item.id]: event.target.value })
                      }
                    />
                  </div>
                </div>
              ))}

              <Input
                label="Supplier delivery note"
                value={deliveryNote}
                onChange={(event) => setDeliveryNote(event.target.value)}
              />

              <div className="flex justify-end gap-2">
                <Button size="sm" variant="ghost" onClick={() => setReceiving(false)}>Cancel</Button>
                <Button
                  size="sm"
                  variant="outline"
                  loading={receiptState.isLoading}
                  onClick={() => void receive()}
                >
                  Receive and put away
                </Button>
              </div>
            </div>
          )}

          <Table>
            <thead>
              <tr>
                <Th>Item</Th>
                <Th numeric>Ordered</Th>
                <Th numeric>Received</Th>
                <Th numeric>Outstanding</Th>
                <Th numeric>Unit cost</Th>
                <Th numeric>Total</Th>
              </tr>
            </thead>
            <tbody>
              {order.items?.map((item) => (
                <tr key={item.id}>
                  <Td>{item.description}</Td>
                  <Td numeric className="text-secondary">
                    {formatQuantity(item.quantity, format)}
                  </Td>
                  <Td numeric className="text-secondary">
                    {formatQuantity(item.receivedQty, format)}
                  </Td>
                  <Td
                    numeric
                    className={Number(item.outstandingQty) > 0 ? 'text-amber-600' : 'text-secondary'}
                  >
                    {formatQuantity(item.outstandingQty, format)}
                  </Td>
                  <Td numeric className="text-secondary">
                    {formatMoney(item.unitCost, format)}
                  </Td>
                  <Td numeric>{formatMoney(item.lineTotal, format)}</Td>
                </tr>
              ))}
            </tbody>
          </Table>

          <div className="ml-auto w-full max-w-xs space-y-1 text-sm">
            <Row label="Subtotal" value={formatMoney(order.subtotal, format)} />
            {Number(order.taxAmount) > 0 && (
              <Row label="Tax" value={formatMoney(order.taxAmount, format)} />
            )}
            {Number(order.shippingAmount) > 0 && (
              <Row label="Shipping" value={formatMoney(order.shippingAmount, format)} />
            )}
            <Row label="Total" value={formatMoney(order.total, format)} strong />
          </div>

          {order.receipts && order.receipts.length > 0 && (
            <div>
              <h3 className="mb-2 text-sm font-medium">Deliveries</h3>
              <ul className="space-y-1 text-sm text-secondary">
                {order.receipts.map((receipt) => (
                  <li key={receipt.id}>
                    {receipt.number} · {formatDate(receipt.receivedAt, format)} ·{' '}
                    {formatMoney(receipt.totalCost, format)} ·{' '}
                    <span className="text-xs">{receipt.status.toLowerCase()}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>
      ) : null}

      {approving && (
        <Modal
          open
          onClose={() => setApproving(false)}
          title="Approve this order"
          description="Approving commits the business to paying for it."
          size="sm"
          footer={
            <>
              <Button variant="outline" onClick={() => setApproving(false)}>Cancel</Button>
              <Button
                loading={actionState.isLoading}
                disabled={!payingAccountId}
                onClick={() => {
                  void act('approve', { accountId: payingAccountId }).then(() =>
                    setApproving(false),
                  );
                }}
              >
                Approve order
              </Button>
            </>
          }
        >
          <div className="space-y-4">
            <Alert tone="info">
              Nothing leaves the account now — the payment does that, against the supplier&rsquo;s
              invoice. Naming it here is what makes the commitment answerable to a balance.
            </Alert>
            <Select
              label="Paid from"
              required
              value={payingAccountId}
              onChange={(event) => setPayingAccountId(event.target.value)}
            >
              <option value="">Select an account</option>
              {accounts?.items.map((account) => (
                <option key={account.id} value={account.id}>
                  {account.name} — {formatMoney(account.balance, format)}
                </option>
              ))}
            </Select>
          </div>
        </Modal>
      )}
    </Modal>
  );
}

function Row({ label, value, strong }: { label: string; value: string; strong?: boolean }) {
  return (
    <div className="flex justify-between">
      <span className="text-muted">{label}</span>
      <span className={strong ? 'font-semibold' : undefined}>{value}</span>
    </div>
  );
}
